import type { Area, AspectRatio, ImageSize } from '../types';

export interface AspectRatioSpec {
  ratio: AspectRatio;
  w: number;
  h: number;
}

export interface ImageSizeSpec {
  key: ImageSize;
  apiValue: string;
  factor: number;
}

export const ASPECT_RATIOS: AspectRatioSpec[] = [
  { ratio: '1:1', w: 512, h: 512 },
  { ratio: '2:3', w: 424, h: 632 },
  { ratio: '3:2', w: 632, h: 424 },
  { ratio: '3:4', w: 448, h: 600 },
  { ratio: '4:3', w: 600, h: 448 },
  { ratio: '4:5', w: 464, h: 576 },
  { ratio: '5:4', w: 576, h: 464 },
  { ratio: '9:16', w: 384, h: 688 },
  { ratio: '16:9', w: 688, h: 384 },
  { ratio: '1:4', w: 256, h: 1024 },
  { ratio: '4:1', w: 1024, h: 256 },
  { ratio: '1:8', w: 192, h: 1536 },
  { ratio: '8:1', w: 1536, h: 192 },
  { ratio: '21:9', w: 792, h: 168 },
];

export const ASPECT_MAP: Record<AspectRatio, AspectRatioSpec> = Object.fromEntries(
  ASPECT_RATIOS.map((a) => [a.ratio, a]),
) as Record<AspectRatio, AspectRatioSpec>;

export const IMAGE_SIZES: ImageSizeSpec[] = [
  { key: '0.5K', apiValue: '512', factor: 1 },
  { key: '1K', apiValue: '1K', factor: 2 },
  { key: '2K', apiValue: '2K', factor: 4 },
  { key: '4K', apiValue: '4K', factor: 8 },
];

export const IMAGE_SIZE_MAP: Record<ImageSize, ImageSizeSpec> = Object.fromEntries(
  IMAGE_SIZES.map((s) => [s.key, s]),
) as Record<ImageSize, ImageSizeSpec>;

export const DEFAULT_ASPECT_RATIO: AspectRatio = '1:1';
export const DEFAULT_IMAGE_SIZE: ImageSize = '0.5K';

export function areaDims(area: Pick<Area, 'aspectRatio' | 'imageSize'>): { w: number; h: number } {
  const a = ASPECT_MAP[area.aspectRatio] ?? ASPECT_MAP[DEFAULT_ASPECT_RATIO];
  const f = (IMAGE_SIZE_MAP[area.imageSize] ?? IMAGE_SIZE_MAP[DEFAULT_IMAGE_SIZE]).factor;
  return { w: a.w * f, h: a.h * f };
}

export function dimsFor(ratio: AspectRatio, size: ImageSize): { w: number; h: number } {
  const a = ASPECT_MAP[ratio] ?? ASPECT_MAP[DEFAULT_ASPECT_RATIO];
  const f = (IMAGE_SIZE_MAP[size] ?? IMAGE_SIZE_MAP[DEFAULT_IMAGE_SIZE]).factor;
  return { w: a.w * f, h: a.h * f };
}

export function imageSizeApiValue(size: ImageSize): string {
  return (IMAGE_SIZE_MAP[size] ?? IMAGE_SIZE_MAP[DEFAULT_IMAGE_SIZE]).apiValue;
}
